import { useEffect, useState } from "react";
import { ChartBar } from "@phosphor-icons/react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api } from "../context/AuthContext";
import { Button } from "./ui/button";

const SERIES = [
  { key: "pass", name: "PASS", color: "#34d399" },
  { key: "pass_retry", name: "PASS 2° tent.", color: "#fbbf24" },
  { key: "human_review", name: "Giudizio umano", color: "#818cf8" },
  { key: "skipped", name: "Saltati", color: "#64748b" },
  { key: "fail", name: "FAIL", color: "#f87171" },
];

const SuiteTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{ background: "#0f172a", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, padding: "10px 12px", fontSize: 12 }}>
      <p style={{ color: "#e2e8f0", marginBottom: 6, fontWeight: 600 }}>{label}</p>
      {payload.map((p) => (
        <p key={p.dataKey} style={{ color: p.fill }}>{p.name}: {p.value}</p>
      ))}
      {row.fail_tests?.length > 0 && (
        <p style={{ color: "#f87171", marginTop: 6 }}>Test falliti: n. {row.fail_tests.join(", ")}</p>
      )}
    </div>
  );
};

export const VikiSuiteHistory = () => {
  const [runs, setRuns] = useState(null);
  const [running, setRunning] = useState(false);
  const [launching, setLaunching] = useState(false);
  const isPreview = typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com");

  const loadHistory = () => {
    api.get("/viki/suite/history")
      .then(({ data }) => setRuns(data.runs || []))
      .catch(() => setRuns([]));
  };

  useEffect(() => {
    loadHistory();
    if (!isPreview) return;
    api.get("/viki/suite/run/status").then(({ data }) => setRunning(data.running)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/viki/suite/run/status");
        if (!data.running) {
          setRunning(false);
          toast.success("Suite completata — grafico aggiornato");
          loadHistory();
        }
      } catch { /* riprova al giro dopo */ }
    }, 6000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running]);

  const launchSuite = async () => {
    setLaunching(true);
    try {
      await api.post("/viki/suite/run");
      setRunning(true);
      toast.info("Suite avviata: ~5 minuti, 100 test. Il grafico si aggiorna da solo alla fine.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Avvio non riuscito");
    } finally {
      setLaunching(false);
    }
  };

  if (!runs || runs.length === 0) return null;

  const chartData = runs.map((r) => ({
    ...r,
    label: r.data ? `${r.data.slice(8, 10)}/${r.data.slice(5, 7)} ${r.data.slice(11, 16)}` : "?",
  }));
  const last = runs[runs.length - 1];

  return (
    <div className="mt-8 p-8 rounded-lg bg-surface border border-surface-border animate-fade-up" data-testid="viki-suite-history">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
        <div>
          <ChartBar size={40} weight="duotone" className="text-violet-400 mb-4" />
          <h2 className="font-heading text-2xl font-semibold tracking-tight mb-1">Suite di verifica VIKI — storico esiti</h2>
          <p className="text-sm text-slate-400">
            Esecuzioni complete dei 100 test comportamentali. I "PASS al 2° tentativo" contano a parte:
            se crescono, VIKI sta diventando meno affidabile anche senza FAIL.
          </p>
          {!isPreview && (
            <p className="text-xs text-amber-400/80 mt-2 max-w-xl" data-testid="viki-suite-provenance-note">
              Questi esiti provengono dall'ambiente di preview, fotografati all'ultimo deploy: la suite non
              gira in produzione (scelta del titolare, per non consumare i limiti LLM giornalieri). Stesso
              codice pubblicato = stessi comportamenti verificati.
            </p>
          )}
        </div>
        <div className="text-right flex flex-col items-end gap-3" data-testid="viki-suite-last-run">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-white/40">Ultima esecuzione</p>
            <p className={`font-heading text-3xl font-bold ${last.fail > 0 ? "text-red-400" : "text-emerald-400"}`}>
              {last.fail > 0 ? `${last.fail} FAIL` : "0 FAIL"}
            </p>
          </div>
          {isPreview && (
            <div className="text-right">
              <Button
                data-testid="viki-suite-run-button"
                onClick={launchSuite}
                disabled={running || launching}
                className="h-9 px-5 bg-white text-black hover:bg-gray-200 rounded-full font-semibold text-xs"
              >
                {running ? "Suite in esecuzione..." : launching ? "Avvio..." : "Esegui la suite (100 test)"}
              </Button>
              <p className="text-[10px] text-white/35 mt-1.5 max-w-[240px]">
                Solo in preview: in produzione consumerebbe i limiti LLM giornalieri di chi usa VIKI davvero.
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="h-64 mt-6">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
            <XAxis dataKey="label" tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} tick={{ fill: "#94a3b8", fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip cursor={{ fill: "rgba(255,255,255,0.04)" }} content={<SuiteTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {SERIES.map((s) => (
              <Bar key={s.key} dataKey={s.key} name={s.name} stackId="run" fill={s.color} maxBarSize={44} />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export const VikiSuiteSection = () => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-6">
      <button type="button" onClick={() => setOpen(!open)} data-testid="viki-suite-section-toggle"
        style={{ "--sec-color": "#45E0D0" }}
        className={`w-full text-left p-5 rounded-2xl border backdrop-blur-md transition-all duration-300 ${
          open
            ? "bg-white/[0.07] border-[var(--sec-color)] shadow-[0_10px_36px_-14px_var(--sec-color)]"
            : "bg-white/[0.03] border-white/10 hover:border-white/25 hover:-translate-y-0.5"
        }`}>
        <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl mb-3" style={{ backgroundColor: "#45E0D01A" }}>
          <ChartBar size={24} weight="duotone" style={{ color: "#45E0D0" }} />
        </span>
        <h3 className="font-heading text-lg font-semibold tracking-tight" style={open ? { color: "#45E0D0" } : undefined}>
          Suite di verifica
        </h3>
        <p className="text-xs text-white/50 mt-1">Storico esiti dei 100 test automatici — clicca per {open ? "chiudere" : "aprire"}</p>
      </button>
      {open && <VikiSuiteHistory />}
    </div>
  );
};
